import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import {
  ForecastData,
  ForecastDay,
  ManagerSetupState,
  PrepItem,
  PrepSubItem,
  SchedulePrepData,
  UptData
} from 'src/types/apps/managerSetup'

dayjs.extend(isoWeek)
dayjs.extend(utc)
dayjs.extend(timezone)

const DEFAULT_RESTAURANT_TZ = 'Australia/Sydney'

/** IANA timezone from Restaurant — falls back to Sydney when missing/invalid. */
export function safeRestaurantTimeZone(raw?: string | null): string {
  const t = (raw ?? '').trim()
  if (!t) return DEFAULT_RESTAURANT_TZ
  const probe = dayjs.tz('2020-06-01', t)

  return probe.isValid() ? t : DEFAULT_RESTAURANT_TZ
}

/** "Last CrunchTime Sync" badge — store-local clock (AU has 4 timezones). */
export function formatCrunchTimeSyncedAt(timeZone?: string | null): string {
  const tz = safeRestaurantTimeZone(timeZone)

  return `Today, ${dayjs().tz(tz).format('h:mm A')}`
}

const DEFAULT_AM_PERCENT = 0

/** Monday (ISO) for the selected week offset — in the restaurant's timezone. */
export function prepWeekMonday(weekOffset = 0, timeZone?: string | null): dayjs.Dayjs {
  const tz = safeRestaurantTimeZone(timeZone)

  return dayjs()
    .tz(tz)
    .startOf('isoWeek')
    .add(weekOffset * 7, 'day')
}

/** Earliest selectable week offset (current week only for now). */
export const MIN_WEEK_OFFSET = 0

/** Only the current picker week (offset 0) allows Tab 1 edits and save/submit. */
export function isManagerPrepEditableWeek(weekOffset = 0): boolean {
  return weekOffset === 0
}

/**
 * Tab 1 row-level editability for the current week.
 * Past days lock once the restaurant-local calendar day has passed.
 * Today and future days remain editable.
 */
export function isForecastDayEditable(dayDate: string, weekOffset = 0, timeZone?: string | null): boolean {
  if (!isManagerPrepEditableWeek(weekOffset)) return false
  const tz = safeRestaurantTimeZone(timeZone)
  const today = dayjs().tz(tz).startOf('day')
  const target = dayjs.tz(dayDate, tz).startOf('day')
  if (!target.isValid()) return false

  return !target.isBefore(today)
}

/** Selectable week offsets — current week only until historical weeks are re-enabled. */
export function selectableWeekOffsets(): number[] {
  return [0]
}

/** Empty Mon–Sun skeleton — Tab 1 values come from Fabric via BE (no fake seed). */
function buildEmptyWeekDays(weekOffset = 0, timeZone?: string | null): ForecastDay[] {
  const monday = prepWeekMonday(weekOffset, timeZone)

  return Array.from({ length: 7 }, (_, i) => {
    const date = monday.add(i, 'day')

    return {
      date: date.format('YYYY-MM-DD'),
      day: date.format('dddd D MMM'),
      ctForecast: 0,
      amPercent: DEFAULT_AM_PERCENT,
      amPercentOverride: null
    }
  })
}

/** Loose match for schedule hydrate (legacy slug keys vs section-N / display names). */
function scheduleKeyMatch(id: string, label: string, key?: string | null): boolean {
  if (!key) return false
  if (id === key || label === key) return true
  const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '')
  const nk = norm(key)
  const ni = norm(id)
  const nl = norm(label)
  if (!nk) return false
  if (ni === nk || nl === nk) return true

  // "prep-in-advance" ↔ "3. Prep in Advance"
  return nl.includes(nk) || nk.includes(nl)
}

function applySavedScheduleFields(
  target: {
    sectionSchedulingBehavior?: 'ONCE_DAILY' | 'AM_PM_SEPARATE' | 'AM_PM_COMBINE' | null
    onceDailyPrepPeriod?: 'AM' | 'PM' | null
    hasAmPrep?: boolean
    hasPmPrep?: boolean
    hasCombinePrep?: boolean
    amPrepTime?: string
    pmPrepTime?: string
    combinePrep?: boolean
    combinePrepTime?: string
    amPrepTimeOverride?: string | null
    pmPrepTimeOverride?: string | null
    combinePrepTimeOverride?: string | null
  },
  saved: {
    sectionSchedulingBehavior?: 'ONCE_DAILY' | 'AM_PM_SEPARATE' | 'AM_PM_COMBINE' | null
    sectionOnceDailyPrepPeriod?: 'AM' | 'PM' | null
    amPrepTime?: string | null
    pmPrepTime?: string | null
    combinePrep?: boolean
    combinePrepTime?: string | null
  },

  // (3 Sep 2026) Section-header rows only. A value present in THIS WEEK'S
  // own submission (draft or submitted) is genuine Manager-entered data —
  // mirror it into the override-tracking field too, so restoring an
  // in-progress draft doesn't lose provenance and a later re-submit still
  // publishes it as a real override. Never set for ordinary ingredient
  // sub-items, which don't have (or need) an override-tracking field.
  isHeaderRow = false
) {
  if (saved.sectionSchedulingBehavior) {
    target.sectionSchedulingBehavior = saved.sectionSchedulingBehavior
    target.onceDailyPrepPeriod =
      saved.sectionSchedulingBehavior === 'ONCE_DAILY' ? (saved.sectionOnceDailyPrepPeriod ?? 'AM') : null
    if (saved.sectionSchedulingBehavior === 'ONCE_DAILY') {
      const showPm = target.onceDailyPrepPeriod === 'PM'
      target.hasAmPrep = !showPm
      target.hasPmPrep = showPm
      target.hasCombinePrep = false
    } else {
      target.hasAmPrep = true
      target.hasPmPrep = true
      target.hasCombinePrep = saved.sectionSchedulingBehavior === 'AM_PM_COMBINE'
    }
  }
  target.amPrepTime = saved.amPrepTime ?? undefined
  target.pmPrepTime = saved.pmPrepTime ?? undefined
  target.combinePrep = Boolean(saved.combinePrep)
  target.combinePrepTime = saved.combinePrepTime ?? undefined
  if (isHeaderRow) {
    target.amPrepTimeOverride = saved.amPrepTime ?? null
    target.pmPrepTimeOverride = saved.pmPrepTime ?? null
    target.combinePrepTimeOverride = saved.combinePrepTime ?? null
  }
}

/** Bulk-sync combine flag + time on first checkbox only. Later row edits stay local. */
function syncCombinePrepGroup(item: PrepItem, combinePrep: boolean, combinePrepTime?: string) {
  item.combinePrep = combinePrep
  item.subItems?.forEach(sub => {
    sub.combinePrep = combinePrep
  })

  if (!combinePrep) return

  const time = combinePrepTime || item.combinePrepTime || item.amPrepTime || item.pmPrepTime
  if (!time) return

  item.combinePrepTime = time

  // (3 Sep 2026) Mirror into the override-tracking field too — this is a
  // real Manager-driven change to the section header's combine time, so it
  // must be sent back on Submit as an explicit override, never dropped as
  // "just the inherited default". See PrepItem.combinePrepTimeOverride.
  item.combinePrepTimeOverride = time
  item.subItems?.forEach(sub => {
    sub.combinePrepTime = time
  })
}

/** After hydrate: turn combine on for the group; fill blank child times, never overwrite saved ones. */
function syncHydratedCombineGroups(schedule: SchedulePrepData) {
  schedule.sections.forEach(section => {
    section.items.forEach(item => {
      const isCombineGroup = Boolean(item.hasCombinePrep || item.subItems?.some(sub => sub.hasCombinePrep))
      if (!isCombineGroup || !item.subItems?.length) return

      const groupCombined = item.combinePrep || item.subItems.some(sub => sub.combinePrep)
      if (!groupCombined) return

      item.combinePrep = true
      if (!item.combinePrepTime) {
        const fromChild = item.subItems.find(sub => sub.combinePrepTime)
        if (fromChild?.combinePrepTime) item.combinePrepTime = fromChild.combinePrepTime
      }

      item.subItems.forEach(sub => {
        sub.combinePrep = true
        if (!sub.combinePrepTime && item.combinePrepTime) {
          sub.combinePrepTime = item.combinePrepTime
        }
      })
    })
  })
}

// Normalize API response (dayDate/dayLabel) → frontend shape (date/day)
function normalizeForecastDays(days: any[]): ForecastDay[] {
  return days.map(d => ({
    date: d.date ?? d.dayDate?.substring(0, 10) ?? '',
    day: d.day ?? d.dayLabel ?? '',
    ctForecast: d.ctForecast ?? 0,
    managerOverride: d.managerOverride ?? undefined,

    // Whole percent — avoid long decimals like 29.22222
    amPercent: Math.round(Number(d.amPercent ?? DEFAULT_AM_PERCENT)),
    amPercentOverride:
      d.amPercentOverride == null || d.amPercentOverride === '' ? null : Math.round(Number(d.amPercentOverride))
  }))
}

/** Effective Tab 1 AM% = explicit manager override ?? live Fabric baseline. */
export function effectiveForecastAmPercent(day: Pick<ForecastDay, 'amPercent' | 'amPercentOverride'>): number {
  if (day.amPercentOverride != null && day.amPercentOverride !== undefined) {
    return Math.round(Number(day.amPercentOverride))
  }

  return Math.round(Number(day.amPercent ?? DEFAULT_AM_PERCENT))
}

/**
 * Persistable AM override: null when unset or equal to the Fabric baseline.
 * Keeps historical rows without an explicit edit from becoming overrides.
 */
export function resolveAmPercentOverrideForSave(
  fabricAmPercent: number,
  amPercentOverride: number | null | undefined
): number | null {
  if (amPercentOverride == null || amPercentOverride === undefined) return null
  const fabric = Math.round(Number(fabricAmPercent) || 0)
  const override = Math.round(Number(amPercentOverride))
  if (override === fabric) return null

  return override
}

/** Stable dirty snapshot for Tab 1 editable values only. */
export function buildForecastDirtySnapshot(forecastData: ForecastData | null | undefined): string {
  return JSON.stringify(
    (forecastData?.days ?? []).map(day => ({
      date: day.date,
      managerOverride: day.managerOverride ?? null,
      amPercentOverride: day.amPercentOverride ?? null
    }))
  )
}

/** Stable dirty snapshot for Tab 2's user-editable override values only. */
export function buildUptDirtySnapshot(uptData: UptData | null | undefined): string {
  return JSON.stringify(
    (uptData?.sections ?? []).map(section => ({
      name: section.name,
      items: (section.items ?? []).map(item => ({
        id: item.id,
        override: item.override ?? null
      }))
    }))
  )
}

function normalizeScheduleTimeForDirty(value: string | null | undefined, fallback: string): string {
  const trimmed = (value ?? '').trim()

  return trimmed || fallback
}

/** Stable dirty snapshot for Tab 3 effective editable values only. */
export function buildScheduleDirtySnapshot(schedulePrepData: SchedulePrepData | null | undefined): string {
  return JSON.stringify(
    (schedulePrepData?.sections ?? []).map(section => ({
      name: section.name,
      items: (section.items ?? []).map(item => ({
        id: item.id,
        amPrepTime: normalizeScheduleTimeForDirty(item.amPrepTime, '10:00 am'),
        pmPrepTime: normalizeScheduleTimeForDirty(item.pmPrepTime, '4:00 pm'),
        combinePrep: item.combinePrep ?? false,
        combinePrepTime: normalizeScheduleTimeForDirty(item.combinePrepTime, '10:00 am'),
        subItems: (item.subItems ?? []).map(sub => ({
          id: sub.id,
          amPrepTime: normalizeScheduleTimeForDirty(sub.amPrepTime, '10:00 am'),
          pmPrepTime: normalizeScheduleTimeForDirty(sub.pmPrepTime, '4:00 pm'),
          combinePrep: sub.combinePrep ?? false,
          combinePrepTime: normalizeScheduleTimeForDirty(sub.combinePrepTime, '10:00 am')
        }))
      }))
    }))
  )
}

/** Explicit Save preserves Submitted current-week status; otherwise stays Draft. */
export function resolveExplicitSaveStatus(
  currentSubmissionStatus?: 'draft' | 'submitted' | null
): 'draft' | 'submitted' {
  return currentSubmissionStatus === 'submitted' ? 'submitted' : 'draft'
}

/** Per-tab Save enablement — only the active tab's dirty state matters. */
export function isManagerSetupSaveEnabled(
  activeStep: number,
  dirtyState: { forecast: boolean; upt: boolean; schedule: boolean }
): boolean {
  if (activeStep === 0) return dirtyState.forecast
  if (activeStep === 1) return dirtyState.upt
  if (activeStep === 2) return dirtyState.schedule

  return false
}

/**
 * Schedule Prep rows may carry either `sectionSchedulingBehavior` (snapshot /
 * child rows) or legacy reference-data `schedulingBehavior` (section headers).
 */
export function resolveRowSchedulingBehavior(row: {
  sectionSchedulingBehavior?: 'ONCE_DAILY' | 'AM_PM_SEPARATE' | 'AM_PM_COMBINE' | null
  schedulingBehavior?: 'ONCE_DAILY' | 'AM_PM_SEPARATE' | 'AM_PM_COMBINE' | null
}): 'ONCE_DAILY' | 'AM_PM_SEPARATE' | 'AM_PM_COMBINE' | null {
  return row.sectionSchedulingBehavior ?? row.schedulingBehavior ?? null
}

/** Schedule Prep field visibility/editability comes from config daypart flags, not hardcoded names. */
export function resolveScheduleFieldEditability(
  combined: boolean,
  hasAmPrep?: boolean,
  hasPmPrep?: boolean,
  schedulingBehavior?: 'ONCE_DAILY' | 'AM_PM_SEPARATE' | 'AM_PM_COMBINE' | null,
  onceDailyPrepPeriod?: 'AM' | 'PM' | null
): {
  showCombinedField: boolean
  amVisible: boolean
  amEditable: boolean
  pmVisible: boolean
  pmEditable: boolean
} {
  if (!combined && schedulingBehavior === 'ONCE_DAILY') {
    const showPm = onceDailyPrepPeriod === 'PM'

    return {
      showCombinedField: false,
      amVisible: !showPm,
      amEditable: !showPm,
      pmVisible: showPm,
      pmEditable: showPm
    }
  }

  const amVisible = !combined && Boolean(hasAmPrep)
  const pmVisible = !combined && Boolean(hasPmPrep)

  return {
    showCombinedField: combined,
    amVisible,
    amEditable: amVisible,
    pmVisible,
    pmEditable: pmVisible
  }
}

type ReferencePayload = {
  weekOf: string
  ctSyncedAt: string
  forecastData: ForecastData
  uptData: UptData
  schedulePrepData: SchedulePrepData
}

/** Merge saved draft/submission onto Fabric reference data (single paint — no flash). */
function hydrateReferencePayload(
  payload: ReferencePayload,
  submission: any | null,
  weekStartDate: string
): ReferencePayload {
  if (!submission) return payload
  if (submission.weekStartDate && submission.weekStartDate !== weekStartDate) return payload

  const forecastData = payload.forecastData
    ? {
        ...payload.forecastData,
        days: payload.forecastData.days.map(day => ({ ...day }))
      }
    : null
  const uptData = payload.uptData
    ? {
        ...payload.uptData,
        sections: payload.uptData.sections.map(section => ({
          ...section,
          items: section.items.map(item => ({ ...item }))
        }))
      }
    : null
  const schedulePrepData = payload.schedulePrepData
    ? {
        ...payload.schedulePrepData,
        sections: payload.schedulePrepData.sections.map(section => ({
          ...section,
          items: section.items.map(item => ({
            ...item,
            subItems: item.subItems?.map(sub => ({ ...sub }))
          }))
        }))
      }
    : null

  if (forecastData && submission.forecastDays?.length) {
    const savedDays = submission.forecastDays

    savedDays.forEach((saved: any) => {
      const idx = forecastData.days.findIndex(d => d.date === saved.dayDate?.substring(0, 10))
      if (idx >= 0) {
        // Never overwrite live Fabric ctForecast / amPercent with saved baselines.
        forecastData.days[idx].managerOverride =
          saved.managerOverride === undefined || saved.managerOverride === null ? undefined : saved.managerOverride
        if (saved.amPercentOverride != null && saved.amPercentOverride !== '') {
          forecastData.days[idx].amPercentOverride = Math.round(Number(saved.amPercentOverride))
        } else {
          forecastData.days[idx].amPercentOverride = null
        }
      }
    })
  }

  if (uptData && submission.uptItems?.length) {
    submission.uptItems.forEach((saved: any) => {
      uptData.sections.forEach(section => {
        if (section.name === saved.sectionName) {
          const item = section.items.find(i => i.id === saved.itemKey)
          if (item) {
            item.override =
              saved.managerOverride === undefined ||
              saved.managerOverride === null ||
              Number(saved.managerOverride) === 0
                ? undefined
                : saved.managerOverride
          }
        }
      })
    })
  }

  if (schedulePrepData && submission.scheduleItems?.length) {
    // SUBMITTED weeks keep the frozen snapshot. Draft / in-progress weeks
    // follow live Prep Config section scheduling so Config edits (e.g.
    // COMBINE) are not overridden by stale per-child schedule rows.
    const preferLiveConfig = submission.status !== 'SUBMITTED'
    const savedSectionHeaders = submission.scheduleItems.filter((saved: any) => !saved.parentItemKey)

    submission.scheduleItems.forEach((saved: any) => {
      schedulePrepData.sections.forEach(section => {
        if (section.name !== saved.sectionName) return

        if (saved.parentItemKey) {
          const parent =
            section.items.find(i => scheduleKeyMatch(i.id, i.item, saved.parentItemKey)) ??
            section.items.find(i =>
              i.subItems?.some(
                s => scheduleKeyMatch(s.id, s.item, saved.itemKey) || scheduleKeyMatch(s.id, s.item, saved.itemLabel)
              )
            )
          const sub = parent?.subItems?.find(
            s => scheduleKeyMatch(s.id, s.item, saved.itemKey) || scheduleKeyMatch(s.id, s.item, saved.itemLabel)
          )
          if (!sub) return

          const savedHeader = savedSectionHeaders.find(
            (header: any) =>
              header.sectionName === section.name &&
              parent &&
              (scheduleKeyMatch(parent.id, parent.item, header.itemKey) ||
                scheduleKeyMatch(parent.id, parent.item, header.itemLabel))
          )

          // Tab 3 placement is section-owned. Never prefer per-child saved
          // sectionSchedulingBehavior — siblings must not diverge (AM vs PM).
          const authoritativeSectionBehavior = preferLiveConfig
            ? (parent?.sectionSchedulingBehavior ??
              savedHeader?.sectionSchedulingBehavior ??
              sub.sectionSchedulingBehavior ??
              null)
            : (savedHeader?.sectionSchedulingBehavior ??
              parent?.sectionSchedulingBehavior ??
              sub.sectionSchedulingBehavior ??
              null)

          if (authoritativeSectionBehavior) {
            // Times come from the section header (or live parent), never from
            // a sibling's private saved am/pm — `?? saved.amPrepTime` was
            // resurrecting stale AM on one child while others stayed empty.
            const sectionSource = savedHeader ?? parent
            const onceDailyPrepPeriod =
              authoritativeSectionBehavior === 'ONCE_DAILY'
                ? preferLiveConfig
                  ? ((parent as any)?.onceDailyPrepPeriod ??
                    savedHeader?.sectionOnceDailyPrepPeriod ??
                    (sectionSource as any)?.onceDailyPrepPeriod ??
                    null)
                  : (savedHeader?.sectionOnceDailyPrepPeriod ??
                    (sectionSource as any)?.sectionOnceDailyPrepPeriod ??
                    (sectionSource as any)?.onceDailyPrepPeriod ??
                    null)
                : null

            let amPrepTime = sectionSource != null ? (sectionSource.amPrepTime ?? null) : saved.amPrepTime
            let pmPrepTime = sectionSource != null ? (sectionSource.pmPrepTime ?? null) : saved.pmPrepTime
            if (authoritativeSectionBehavior === 'ONCE_DAILY') {
              if (onceDailyPrepPeriod === 'PM') amPrepTime = null
              else pmPrepTime = null
            }

            applySavedScheduleFields(sub, {
              ...saved,
              sectionSchedulingBehavior: authoritativeSectionBehavior,
              sectionOnceDailyPrepPeriod: onceDailyPrepPeriod,
              amPrepTime,
              pmPrepTime,
              combinePrep:
                authoritativeSectionBehavior === 'AM_PM_COMBINE'
                  ? sectionSource != null
                    ? Boolean(sectionSource.combinePrep)
                    : Boolean(saved.combinePrep)
                  : false,
              combinePrepTime:
                authoritativeSectionBehavior === 'AM_PM_COMBINE'
                  ? sectionSource != null
                    ? (sectionSource.combinePrepTime ?? null)
                    : saved.combinePrepTime
                  : null
            })

            return
          }

          applySavedScheduleFields(sub, saved)

          return
        }

        // Section-header row (no parentItemKey). GET already hydrated this
        // header's display value + override provenance fresh via
        // resolveSectionScheduleTimeWithProvenance (see toPrepItems on the
        // backend) — but if THIS WEEK's own submission (draft or
        // submitted) already recorded a real value the Manager typed, that
        // genuine in-progress edit should win, and must carry its
        // provenance forward too (`isHeaderRow = true`) so a later
        // re-submit still publishes it as an explicit override rather than
        // silently reverting to whatever GET resolved.
        const item = section.items.find(
          i => scheduleKeyMatch(i.id, i.item, saved.itemKey) || scheduleKeyMatch(i.id, i.item, saved.itemLabel)
        )
        if (!item) return

        if (preferLiveConfig) {
          const liveBehavior = item.sectionSchedulingBehavior ?? (item as any).schedulingBehavior ?? null
          applySavedScheduleFields(
            item,
            {
              ...saved,
              sectionSchedulingBehavior: liveBehavior ?? saved.sectionSchedulingBehavior,
              sectionOnceDailyPrepPeriod:
                (liveBehavior ?? saved.sectionSchedulingBehavior) === 'ONCE_DAILY'
                  ? (item.onceDailyPrepPeriod ?? saved.sectionOnceDailyPrepPeriod ?? null)
                  : null
            },
            true
          )
        } else {
          applySavedScheduleFields(item, saved, true)
        }
      })
    })
    syncHydratedCombineGroups(schedulePrepData)
  }

  return {
    ...payload,
    forecastData: forecastData ?? payload.forecastData,
    uptData: uptData ?? payload.uptData,
    schedulePrepData: schedulePrepData ?? payload.schedulePrepData
  }
}

// ─── Local draft helpers ──────────────────────────────────────────────────────
const lsKey = (restaurantId: string) => `manager-prep-draft-${restaurantId}`

function saveDraftLocally(restaurantId: string, payload: any) {
  try {
    localStorage.setItem(lsKey(restaurantId), JSON.stringify({ ...payload, savedAt: new Date().toISOString() }))
  } catch {
    /* storage quota */
  }
}

function loadDraftLocally(restaurantId: string) {
  try {
    const raw = localStorage.getItem(lsKey(restaurantId))

    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

async function loadWeekSubmission(
  httpClient: { get: (url: string, config?: object) => Promise<{ data: any }> },
  restaurantId: string,
  weekOffset: number,
  timeZone?: string | null
): Promise<{ submission: any | null; weekStartDate: string }> {
  const weekStartDate = prepWeekMonday(weekOffset, timeZone).format('YYYY-MM-DD')

  try {
    const { data } = await httpClient.get(`/preplists/restaurant/${restaurantId}/manager/submissions/current-week`, {
      params: { weekOffset }
    })
    const submission = data?.data ?? data
    if (submission?.weekStartDate || submission?.scheduleItems || submission?.forecastDays) {
      return { submission, weekStartDate }
    }
  } catch {
    /* fall through to local draft */
  }

  const local = loadDraftLocally(restaurantId)
  if (local?.weekStartDate === weekStartDate) {
    return { submission: local, weekStartDate }
  }

  return { submission: null, weekStartDate }
}

// ─── Reference data ──────────────────────────────────────────────────────────
// Tab 1 forecast + Tab 2 current-week UPT from Fabric via BE. Tab 3 from config + seed.

export const fetchReferenceData = createAsyncThunk(
  'managerSetup/fetchReferenceData',
  async (
    { restaurantId, weekOffset = 0, timeZone }: { restaurantId: string; weekOffset?: number; timeZone?: string | null },
    { extra }: any
  ) => {
    const monday = prepWeekMonday(weekOffset, timeZone)
    const weekOf = monday.format('D MMM')
    const ctSyncedAt = formatCrunchTimeSyncedAt(timeZone)
    const weekStartDate = monday.format('YYYY-MM-DD')

    let basePayload: ReferencePayload

    try {
      const [{ data }, submissionResult] = await Promise.all([
        extra.httpClient.get(`/preplists/restaurant/${restaurantId}/manager/reference-data`, {
          params: { weekOffset }
        }),
        loadWeekSubmission(extra.httpClient, restaurantId, weekOffset, timeZone)
      ])

      const payload = data?.data ?? data
      if (!payload?.forecastData) {
        throw new Error('reference-data missing forecastData')
      }

      basePayload = {
        ...payload,
        forecastData: {
          ...payload.forecastData,
          days: normalizeForecastDays(payload.forecastData?.days ?? [])
        }
      }

      return {
        ...hydrateReferencePayload(basePayload, submissionResult.submission, weekStartDate),
        currentSubmissionStatus: submissionResult.submission?.status ?? null
      }
    } catch {
      const submissionResult = await loadWeekSubmission(extra.httpClient, restaurantId, weekOffset, timeZone)
      basePayload = {
        weekOf,
        ctSyncedAt,
        forecastData: { weekOf, ctSyncedAt, days: buildEmptyWeekDays(weekOffset, timeZone) } as ForecastData,
        uptData: {
          weekOf,
          ctSyncedAt,
          sections: []
        } as UptData,
        schedulePrepData: {
          weekOf,
          ctSyncedAt,
          sections: [
            { name: 'Cook Prep', items: [] },
            { name: 'Line Prep', items: [] }
          ]
        } as SchedulePrepData
      }

      return {
        ...hydrateReferencePayload(basePayload, submissionResult.submission, weekStartDate),
        currentSubmissionStatus: submissionResult.submission?.status ?? null
      }
    }
  }
)

// ─── Load existing draft for current week (if any) ───────────────────────────

export const fetchCurrentWeekSubmission = createAsyncThunk(
  'managerSetup/fetchCurrentWeekSubmission',
  async (
    { restaurantId, weekOffset = 0, timeZone }: { restaurantId: string; weekOffset?: number; timeZone?: string | null },
    { extra }: any
  ) => {
    const { submission, weekStartDate } = await loadWeekSubmission(extra.httpClient, restaurantId, weekOffset, timeZone)

    return { submission, requestedWeekOffset: weekOffset, weekStartDate }
  }
)

// ─── Save (draft or submit) ───────────────────────────────────────────────────

export const saveManagerPrep = createAsyncThunk(
  'managerSetup/saveManagerPrep',
  async (
    {
      restaurantId,
      payload
    }: {
      restaurantId: string
      payload: {
        weekStartDate: string
        status?: 'draft' | 'submitted'
        forecastDays: any[]
        uptItems: any[]
        scheduleItems: any[]
      }
    },
    { extra, rejectWithValue }: any
  ) => {
    // Always persist locally so data survives page refresh / offline
    saveDraftLocally(restaurantId, payload)

    try {
      const { data } = await extra.httpClient.post(`/preplists/restaurant/${restaurantId}/manager/submissions`, payload)
      const saved = data?.data ?? data
      if (!saved) {
        return rejectWithValue('Save succeeded but no submission was returned')
      }

      return saved
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Failed to save prep to server (kept locally)'

      return rejectWithValue(Array.isArray(message) ? message.join(', ') : message)
    }
  }
)

// ─── Slice ────────────────────────────────────────────────────────────────────

const initialState: ManagerSetupState = {
  activeStep: 0,
  weekOffset: 0,
  currentSubmissionStatus: null,
  forecastData: null,
  uptData: null,
  schedulePrepData: null,
  loading: false,
  saving: false,
  errors: null,
  savedSteps: [false, false, false]
}

const managerSetupSlice = createSlice({
  name: 'managerSetup',
  initialState,
  reducers: {
    setActiveStep(state, action: PayloadAction<number>) {
      state.activeStep = action.payload
    },
    setWeekOffset(state, action: PayloadAction<number>) {
      state.weekOffset = action.payload

      // Keep tab payloads visible — tabs render in-table skeleton while refetching.
      state.loading = true
      state.currentSubmissionStatus = null
    },
    updateForecastDay(
      state,
      action: PayloadAction<{
        index: number
        field: 'managerOverride' | 'amPercent'
        value: number | undefined
      }>
    ) {
      if (!state.forecastData) return
      const { index, field, value } = action.payload
      const day = state.forecastData.days[index]
      if (!day) return

      if (field === 'amPercent') {
        // Keep Fabric baseline in amPercent; store edits in amPercentOverride.
        const fabric = Math.round(Number(day.amPercent) || 0)
        if (value === undefined || value === null) {
          day.amPercentOverride = null

          return
        }
        const next = Math.round(Number(value))
        day.amPercentOverride = next === fabric ? null : next

        return
      }

      day.managerOverride = value as number
    },
    updateUptOverride(state, action: PayloadAction<{ sectionIndex: number; itemIndex: number; value?: number }>) {
      if (!state.uptData) return
      const { sectionIndex, itemIndex, value } = action.payload
      state.uptData.sections[sectionIndex].items[itemIndex].override = value
    },
    updatePrepItem(
      state,
      action: PayloadAction<{
        sectionIndex: number
        itemIndex: number
        field: keyof PrepItem
        value: string | boolean | undefined
      }>
    ) {
      if (!state.schedulePrepData) return
      const { sectionIndex, itemIndex, field, value } = action.payload
      const item = state.schedulePrepData.sections[sectionIndex].items[itemIndex]
      ;(item as any)[field] = value

      // (3 Sep 2026) A direct edit to a header row's own time field is a
      // real Manager change — mirror it into the override-tracking field so
      // Submit publishes it as an explicit override, not an inherited
      // value. See PrepItem.amPrepTimeOverride/pmPrepTimeOverride/combinePrepTimeOverride.
      if (field === 'amPrepTime' || field === 'pmPrepTime' || field === 'combinePrepTime') {
        ;(item as any)[`${field}Override`] = value
      }
      if (field === 'combinePrepTime' && item.subItems?.length) {
        item.subItems.forEach(sub => {
          sub.combinePrepTime = value as string
        })
      }
    },
    applyCombinePrepToGroup(
      state,
      action: PayloadAction<{
        sectionIndex: number
        itemIndex: number
        combinePrep: boolean
        combinePrepTime?: string
      }>
    ) {
      if (!state.schedulePrepData) return
      const { sectionIndex, itemIndex, combinePrep, combinePrepTime } = action.payload
      const item = state.schedulePrepData.sections[sectionIndex].items[itemIndex]
      syncCombinePrepGroup(item, combinePrep, combinePrepTime)
    },
    updatePrepSubItem(
      state,
      action: PayloadAction<{
        sectionIndex: number
        itemIndex: number
        subItemIndex: number
        field: keyof PrepSubItem
        value: string | boolean | undefined
      }>
    ) {
      if (!state.schedulePrepData) return
      const { sectionIndex, itemIndex, subItemIndex, field, value } = action.payload
      const subItems = state.schedulePrepData.sections[sectionIndex].items[itemIndex].subItems
      if (subItems) (subItems[subItemIndex] as any)[field] = value
    },
    applyPrepTimeToSubItems(
      state,
      action: PayloadAction<{
        sectionIndex: number
        itemIndex: number
        field: 'amPrepTime' | 'pmPrepTime'
        value: string
      }>
    ) {
      if (!state.schedulePrepData) return
      const { sectionIndex, itemIndex, field, value } = action.payload
      const item = state.schedulePrepData.sections[sectionIndex].items[itemIndex]
      item[field] = value

      // (3 Sep 2026) Real Manager edit to the header's own AM/PM time —
      // mirror into the override-tracking field (subItems don't have one;
      // they publish their own per-ingredient time, unrelated to the
      // restaurant schedule override table).
      item[field === 'amPrepTime' ? 'amPrepTimeOverride' : 'pmPrepTimeOverride'] = value
      item.subItems?.forEach(sub => {
        sub[field] = value
      })
    },
    markStepSaved(state, action: PayloadAction<number>) {
      state.savedSteps[action.payload] = true
    },
    resetManagerSetup(state) {
      state.activeStep = 0
      state.weekOffset = 0
      state.currentSubmissionStatus = null
      state.savedSteps = [false, false, false]
      state.errors = null
      state.forecastData = null
      state.uptData = null
      state.schedulePrepData = null

      // Show spinner until fetchReferenceData settles
      state.loading = true
    }
  },
  extraReducers: builder => {
    // fetchReferenceData
    builder
      .addCase(fetchReferenceData.pending, state => {
        state.loading = true
      })
      .addCase(fetchReferenceData.fulfilled, (state, action) => {
        state.loading = false
        state.currentSubmissionStatus = action.payload.currentSubmissionStatus ?? null
        state.forecastData = action.payload.forecastData
        state.uptData = action.payload.uptData
        state.schedulePrepData = action.payload.schedulePrepData
      })
      .addCase(fetchReferenceData.rejected, (state, action) => {
        state.loading = false
        state.errors = action.error
      })

    // fetchCurrentWeekSubmission — manual re-hydrate (e.g. after save); page load merges in fetchReferenceData
    builder
      .addCase(fetchCurrentWeekSubmission.fulfilled, (state, action) => {
        const { submission, requestedWeekOffset, weekStartDate } = action.payload
        if (requestedWeekOffset !== state.weekOffset) return
        state.currentSubmissionStatus = submission?.status ?? null
        if (!submission || !state.forecastData || !state.uptData || !state.schedulePrepData) return

        const hydrated = hydrateReferencePayload(
          {
            weekOf: state.forecastData.weekOf,
            ctSyncedAt: state.forecastData.ctSyncedAt,
            forecastData: state.forecastData,
            uptData: state.uptData,
            schedulePrepData: state.schedulePrepData
          },
          submission,
          weekStartDate
        )
        state.forecastData = hydrated.forecastData
        state.uptData = hydrated.uptData
        state.schedulePrepData = hydrated.schedulePrepData
      })
      .addCase(fetchCurrentWeekSubmission.rejected, (state, action) => {
        state.loading = false
        state.errors = action.error
      })

    // saveManagerPrep
    builder
      .addCase(saveManagerPrep.pending, state => {
        state.saving = true
      })
      .addCase(saveManagerPrep.fulfilled, (state, action) => {
        state.saving = false
        state.currentSubmissionStatus = action.payload?.status ?? state.currentSubmissionStatus ?? null
      })
      .addCase(saveManagerPrep.rejected, (state, action) => {
        state.saving = false
        state.errors = action.error
      })
  }
})

export const {
  setActiveStep,
  setWeekOffset,
  updateForecastDay,
  updateUptOverride,
  updatePrepItem,
  updatePrepSubItem,
  applyPrepTimeToSubItems,
  applyCombinePrepToGroup,
  markStepSaved,
  resetManagerSetup
} = managerSetupSlice.actions

export default managerSetupSlice.reducer
